import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import QRCode from "react-qr-code";
import { baseApiURL } from "../../../baseUrl";
import {
  FaBed,
  FaIdCard,
  FaUtensils,
  FaTools,
  FaQrcode,
  FaPlus,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaExclamationCircle,
  FaStar,
  FaPhoneAlt,
  FaMoneyBillWave,
  FaInfoCircle,
} from "react-icons/fa";

const StudentHostelPortal = () => {
  const router = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("My Room");
  const [messMenu, setMessMenu] = useState(null);
  const [allMenus, setAllMenus] = useState([]);

  // Gate pass form
  const [showGatePassModal, setShowGatePassModal] = useState(false);
  const [gatePassForm, setGatePassForm] = useState({
    passType: "Day Outing",
    reason: "",
    destination: "",
    expectedOutTime: "",
    expectedInTime: "",
  });

  // Complaint form
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [complaintForm, setComplaintForm] = useState({
    category: "Electrical",
    priority: "Medium",
    title: "",
    description: "",
  });

  // Mess feedback form
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackMealType, setFeedbackMealType] = useState("Lunch");

  const token = localStorage.getItem("token");
  const currentLoginId =
    router.state?.loginid ||
    localStorage.getItem("loginid") ||
    localStorage.getItem("enrollmentNo") ||
    localStorage.getItem("userId") ||
    "";

  useEffect(() => {
    fetchStudentHostelData();
  }, [router.state]);

  const fetchStudentHostelData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const resolvedId =
        router.state?.loginid ||
        localStorage.getItem("loginid") ||
        localStorage.getItem("enrollmentNo") ||
        localStorage.getItem("userId") ||
        "";

      if (resolvedId) {
        try {
          const res = await axios.get(`${baseApiURL()}/hostel/student/${resolvedId}`, { headers });
          if (res.data?.success) {
            setData(res.data);
          }
        } catch (err) {
          console.warn("Student hostel details fetch error:", err.response?.data || err.message);
        }
      }

      try {
        const menuRes = await axios.get(`${baseApiURL()}/hostel/mess/menu`, { headers });
        const menus = menuRes.data?.menus || [];
        setAllMenus(menus);
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const today = days[new Date().getDay()];
        const todayMenu = menus.find((m) => m.dayOfWeek === today) || menus[0];
        setMessMenu(todayMenu);
      } catch (err) {
        console.warn("Mess menu fetch error:", err.message);
      }
    } catch (err) {
      console.error("Fetch Student Hostel Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyGatePass = async (e) => {
    e.preventDefault();
    if (!data?.student?._id) return;
    try {
      const res = await axios.post(
        `${baseApiURL()}/hostel/gatepass/apply`,
        {
          studentId: data.student._id,
          ...gatePassForm,
          parentContactNumber: data.student.fatherPhoneNumber,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Gate pass applied!");
        setShowGatePassModal(false);
        fetchStudentHostelData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to apply for gate pass");
    }
  };

  const handleRaiseComplaint = async (e) => {
    e.preventDefault();
    if (!data?.student?._id) return;
    try {
      const res = await axios.post(
        `${baseApiURL()}/hostel/complaints/raise`,
        {
          studentId: data.student._id,
          ...complaintForm,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Complaint registered!");
        setShowComplaintModal(false);
        fetchStudentHostelData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to raise complaint");
    }
  };

  const handleMessFeedback = async (e) => {
    e.preventDefault();
    if (!data?.student?._id) return;
    try {
      const res = await axios.post(
        `${baseApiURL()}/hostel/mess/feedback`,
        {
          studentId: data.student._id,
          mealType: feedbackMealType,
          rating: feedbackRating,
          comments: feedbackComment,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success("Feedback submitted!");
        setFeedbackComment("");
      }
    } catch (err) {
      toast.error("Failed to submit feedback");
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-xs text-slate-500">Loading your hostel portal...</div>;
  }

  if (!data?.allocation) {
    return (
      <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 text-center space-y-4 max-w-lg mx-auto my-10">
        <div className="w-16 h-16 bg-rose-100 dark:bg-rose-950/50 rounded-2xl flex items-center justify-center mx-auto text-rose-600 text-2xl">
          <FaBed />
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          No Active Hostel Allocation Found
        </h3>
        <p className="text-xs text-slate-500">
          You are currently not allocated to a hostel room. Please contact the Hostel Warden office or submit an application.
        </p>
      </div>
    );
  }

  const { allocation, roommates, recentGatePasses, recentComplaints } = data;
  const activePass = recentGatePasses?.find((gp) => ["Applied", "Approved", "Checked Out"].includes(gp.status));

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-rose-500 via-amber-600 to-amber-500 p-6 rounded-2xl text-white shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2.5 py-1 rounded-full">
            Hosteller Profile
          </span>
          <h2 className="text-xl font-black mt-2">
            {allocation.blockId?.blockName} • Room {allocation.roomId?.roomNumber}
          </h2>
          <p className="text-xs text-rose-100 mt-0.5">
            Bed Code: <span className="font-mono font-bold">{allocation.bedId?.bedCode}</span> • Curfew {allocation.blockId?.curfewTime}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowGatePassModal(true)}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-white text-rose-600 hover:bg-rose-50 cursor-pointer shadow-sm transition"
          >
            <FaIdCard className="inline mr-1" /> Apply Gate Pass
          </button>
          <button
            onClick={() => setShowComplaintModal(true)}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 cursor-pointer shadow-sm transition"
          >
            <FaTools className="inline mr-1" /> Raise Issue
          </button>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="flex gap-2 p-1.5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {["My Room", "Digital Gate Pass", "Mess Menu & Food", "Maintenance Tickets", "Fee Status"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
              activeTab === tab
                ? "bg-rose-600 text-white shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* MY ROOM & ROOMMATES */}
      {activeTab === "My Room" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaBed className="text-rose-500" /> Accommodation Overview
            </h3>
            <div className="space-y-2 text-xs divide-y divide-slate-100 dark:divide-slate-800">
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Hostel Block:</span>
                <span className="font-bold">{allocation.blockId?.blockName}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Room Number:</span>
                <span className="font-bold">{allocation.roomId?.roomNumber}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Room Category:</span>
                <span className="font-bold">{allocation.roomId?.roomType}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Allocated Bed:</span>
                <span className="font-mono font-bold text-rose-600">{allocation.bedId?.bedCode}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500">Night Curfew:</span>
                <span className="font-bold text-amber-600">{allocation.blockId?.curfewTime} PM</span>
              </div>
            </div>
          </div>

          {/* Roommates Card */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaUsers className="text-amber-500" /> Roommates
            </h3>
            {roommates?.length === 0 ? (
              <p className="text-xs text-slate-400">No other roommates allocated in this room yet.</p>
            ) : (
              <div className="space-y-3">
                {roommates.map((rm) => (
                  <div key={rm._id} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-xs">
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-slate-100">
                        {rm.firstName} {rm.lastName}
                      </h4>
                      <span className="text-[11px] text-slate-500 font-mono">
                        {rm.enrollmentNo} • {rm.branch}
                      </span>
                    </div>
                    <span className="font-mono font-bold text-[11px] px-2 py-0.5 rounded bg-rose-100 text-rose-700">
                      {rm.bedCode}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DIGITAL GATE PASS */}
      {activeTab === "Digital Gate Pass" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Active Pass Card */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaIdCard className="text-rose-500" /> Current Gate Pass Status
            </h3>

            {activePass ? (
              <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/20 space-y-4 text-center">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-mono font-bold text-rose-600">#{activePass.passNumber}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                    {activePass.status}
                  </span>
                </div>

                {/* QR Code for Security Gate Scanner */}
                <div className="p-4 bg-white rounded-xl inline-block shadow-xs">
                  <QRCode value={activePass.passNumber} size={140} />
                </div>
                <p className="text-[11px] text-slate-500">
                  Scan this QR code at the Main Security Gate during exit and entry.
                </p>

                <div className="text-xs text-left bg-white dark:bg-slate-900 p-3 rounded-lg space-y-1">
                  <div><span className="font-bold">Pass Type:</span> {activePass.passType}</div>
                  <div><span className="font-bold">Destination:</span> {activePass.destination}</div>
                  <div><span className="font-bold">Out Time:</span> {new Date(activePass.expectedOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  <div><span className="font-bold">In Time:</span> {new Date(activePass.expectedInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-2">
                <FaCheckCircle className="text-3xl text-emerald-500 mx-auto" />
                <p className="text-xs text-slate-500">No active gate pass. You are currently in the hostel.</p>
                <button
                  onClick={() => setShowGatePassModal(true)}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                >
                  Apply for Outing Pass
                </button>
              </div>
            )}
          </div>

          {/* Past Gate Passes */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Past Outing History</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {recentGatePasses?.map((gp) => (
                <div key={gp._id} className="flex justify-between items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-xs">
                  <div>
                    <span className="font-bold text-slate-800 dark:text-slate-200">{gp.destination}</span>
                    <p className="text-[10px] text-slate-500">{gp.passType} • #{gp.passNumber}</p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700">
                    {gp.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MESS MENU & FEEDBACK */}
      {activeTab === "Mess Menu & Food" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Today's Menu */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaUtensils className="text-amber-500" /> Today's Scheduled Dining Menu
            </h3>
            {messMenu ? (
              <div className="space-y-3 text-xs">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <span className="font-bold text-rose-600">🥣 Breakfast ({messMenu.breakfast?.timing})</span>
                  <p className="text-slate-500 mt-1">{messMenu.breakfast?.items?.join(", ")}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <span className="font-bold text-amber-600">🍛 Lunch ({messMenu.lunch?.timing})</span>
                  <p className="text-slate-500 mt-1">{messMenu.lunch?.items?.join(", ")}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <span className="font-bold text-indigo-600">☕ Evening Snacks ({messMenu.snacks?.timing})</span>
                  <p className="text-slate-500 mt-1">{messMenu.snacks?.items?.join(", ")}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <span className="font-bold text-emerald-600">🍲 Dinner ({messMenu.dinner?.timing})</span>
                  <p className="text-slate-500 mt-1">{messMenu.dinner?.items?.join(", ")}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400">No menu posted for today.</p>
            )}
          </div>

          {/* Daily Food Feedback */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaStar className="text-amber-400" /> Food Quality & Hygiene Rating
            </h3>
            <form onSubmit={handleMessFeedback} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Meal</label>
                <select
                  value={feedbackMealType}
                  onChange={(e) => setFeedbackMealType(e.target.value)}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                >
                  <option value="Breakfast">Breakfast</option>
                  <option value="Lunch">Lunch</option>
                  <option value="Snacks">Snacks</option>
                  <option value="Dinner">Dinner</option>
                </select>
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Rating (1 to 5 Stars)</label>
                <div className="flex gap-2 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer ${
                        feedbackRating >= star ? "bg-amber-400 text-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                      }`}
                    >
                      ★ {star}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Comments / Suggestions</label>
                <textarea
                  rows="2"
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  placeholder="Taste, cleanliness, food temperature..."
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                />
              </div>
              <button
                type="submit"
                className="w-full py-2 rounded-xl text-xs font-bold bg-amber-500 text-white cursor-pointer hover:bg-amber-600 transition"
              >
                Submit Feedback
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MAINTENANCE TICKETS */}
      {activeTab === "Maintenance Tickets" && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FaTools className="text-indigo-500" /> My Maintenance Requests
            </h3>
            <button
              onClick={() => setShowComplaintModal(true)}
              className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
            >
              + Raise New Ticket
            </button>
          </div>

          <div className="space-y-3">
            {recentComplaints?.map((c) => (
              <div key={c._id} className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-2 text-xs">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-mono text-indigo-600 font-bold">#{c.ticketId}</span>
                    <h4 className="font-bold text-slate-900 dark:text-slate-100">{c.title}</h4>
                    <span className="text-[10px] text-slate-500">{c.category} • Priority: {c.priority}</span>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${c.status === "Resolved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {c.status}
                  </span>
                </div>
                <p className="text-slate-600 dark:text-slate-300">{c.description}</p>
                {c.resolutionNotes && (
                  <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 text-[11px]">
                    <span className="font-bold">Warden/Technician Note:</span> {c.resolutionNotes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FEE STATUS */}
      {activeTab === "Fee Status" && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 max-w-xl mx-auto">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FaMoneyBillWave className="text-emerald-500" /> Hostel Fee Structure & Receipts
          </h3>
          <div className="space-y-2 text-xs divide-y divide-slate-100 dark:divide-slate-800">
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Room Rent (Semester):</span>
              <span className="font-bold">₹{allocation.feeStructure?.roomRent?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Mess Dining Fee:</span>
              <span className="font-bold">₹{allocation.feeStructure?.messFee?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Refundable Caution Deposit:</span>
              <span className="font-bold">₹{allocation.feeStructure?.cautionDeposit?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Total Hostel Fee:</span>
              <span className="font-bold text-slate-900 dark:text-slate-100">₹{allocation.feeStructure?.totalFee?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2 text-emerald-600 font-bold">
              <span>Paid Amount:</span>
              <span>₹{allocation.feeStructure?.paidAmount?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2 text-rose-600 font-bold">
              <span>Pending Dues:</span>
              <span>₹{allocation.feeStructure?.dueAmount?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* APPLY GATE PASS MODAL */}
      {showGatePassModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Apply Digital Gate Pass</h3>
            <form onSubmit={handleApplyGatePass} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Outing Type</label>
                <select
                  value={gatePassForm.passType}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, passType: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                >
                  <option value="Day Outing">Local Day Outing (Return before curfew)</option>
                  <option value="Weekend Leave">Weekend / Home Leave</option>
                  <option value="Emergency Medical">Emergency / Medical Leave</option>
                  <option value="Official / Academic">Academic / Placement Drive</option>
                </select>
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Destination</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. City Market / Hometown"
                  value={gatePassForm.destination}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, destination: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Reason</label>
                <textarea
                  rows="2"
                  required
                  placeholder="e.g. Shopping, family function, doctor visit..."
                  value={gatePassForm.reason}
                  onChange={(e) => setGatePassForm({ ...gatePassForm, reason: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-bold text-slate-700 dark:text-slate-300">Expected Out Time</label>
                  <input
                    type="datetime-local"
                    required
                    value={gatePassForm.expectedOutTime}
                    onChange={(e) => setGatePassForm({ ...gatePassForm, expectedOutTime: e.target.value })}
                    className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 dark:text-slate-300">Expected In Time</label>
                  <input
                    type="datetime-local"
                    required
                    value={gatePassForm.expectedInTime}
                    onChange={(e) => setGatePassForm({ ...gatePassForm, expectedInTime: e.target.value })}
                    className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowGatePassModal(false)}
                  className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-xl text-xs font-bold bg-rose-600 text-white cursor-pointer"
                >
                  Submit Gate Pass
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RAISE COMPLAINT MODAL */}
      {showComplaintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Raise Maintenance Ticket</h3>
            <form onSubmit={handleRaiseComplaint} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Category</label>
                <select
                  value={complaintForm.category}
                  onChange={(e) => setComplaintForm({ ...complaintForm, category: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                >
                  <option value="Electrical">Electrical (Fan, Light, Switch)</option>
                  <option value="Plumbing">Plumbing (Tap, Shower, Geyser)</option>
                  <option value="Wi-Fi / Internet">Wi-Fi / LAN Internet</option>
                  <option value="Carpentry">Carpentry (Bed, Desk, Lock)</option>
                  <option value="Housekeeping">Housekeeping / Cleaning</option>
                  <option value="Pest Control">Pest Control</option>
                </select>
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Issue Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Geyser not heating in 204"
                  value={complaintForm.title}
                  onChange={(e) => setComplaintForm({ ...complaintForm, title: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 dark:text-slate-300">Description</label>
                <textarea
                  rows="3"
                  required
                  placeholder="Describe the issue in detail..."
                  value={complaintForm.description}
                  onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                  className="w-full mt-1 p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowComplaintModal(false)}
                  className="px-3 py-1.5 rounded-xl border text-xs font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-xl text-xs font-bold bg-indigo-600 text-white cursor-pointer"
                >
                  Register Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentHostelPortal;
